const PANEL_GAP_PX = 8;
const VIEWPORT_PADDING_PX = 8;
/** Fallback when the panel has not been measured yet (calendar + time + actions). */
export const EVENTQ_DATETIME_PICKER_ESTIMATED_PANEL_HEIGHT_PX = 400;

export type EventqDatetimePickerResolvedPlacement = 'above' | 'below';

export interface EventqDatetimePickerPanelStyle {
  position: 'fixed';
  top: string;
  bottom: string;
  left: string;
  right: string;
  /** Omitted when CSS owns panel width (preferred). */
  width?: string;
  zIndex: string;
}

/**
 * Chooses above/below so the panel fits in the viewport when possible.
 * Prefers `preferred` when both sides fit (or neither does, using the larger side).
 */
export function resolveDatetimePickerPlacement(
  preferred: EventqDatetimePickerResolvedPlacement,
  fieldRect: DOMRect,
  panelHeightPx: number,
  viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768,
): EventqDatetimePickerResolvedPlacement {
  const spaceAbove = fieldRect.top - VIEWPORT_PADDING_PX;
  const spaceBelow = viewportHeight - fieldRect.bottom - VIEWPORT_PADDING_PX;
  const preferredSpace = preferred === 'above' ? spaceAbove : spaceBelow;
  const alternate: EventqDatetimePickerResolvedPlacement =
    preferred === 'above' ? 'below' : 'above';
  const alternateSpace = preferred === 'above' ? spaceBelow : spaceAbove;

  if (preferredSpace >= panelHeightPx) {
    return preferred;
  }
  if (alternateSpace >= panelHeightPx) {
    return alternate;
  }
  return preferredSpace >= alternateSpace ? preferred : alternate;
}

/**
 * Positions the field-mode popover in the viewport so flex/overflow ancestors
 * cannot clip the calendar body. Flips above/below when the preferred side
 * does not have enough room.
 *
 * End-align anchors with CSS `right` so the panel’s right edge matches the
 * field’s right edge exactly; panel width comes from component styles.
 * Start-align anchors with CSS `left` to the field’s left edge.
 */
export function computeDatetimePickerPanelStyle(
  fieldRect: DOMRect,
  placement: EventqDatetimePickerResolvedPlacement,
  align: 'start' | 'end',
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024,
  viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768,
  panelHeightPx = EVENTQ_DATETIME_PICKER_ESTIMATED_PANEL_HEIGHT_PX,
  panelWidthPx?: number,
): EventqDatetimePickerPanelStyle {
  const resolved = resolveDatetimePickerPlacement(
    placement,
    fieldRect,
    panelHeightPx,
    viewportHeight,
  );

  const horizontal = resolveHorizontalAnchors(
    fieldRect,
    align,
    viewportWidth,
    panelWidthPx,
  );

  if (resolved === 'below') {
    let topPx = fieldRect.bottom + PANEL_GAP_PX;
    const maxTop = viewportHeight - VIEWPORT_PADDING_PX - Math.min(panelHeightPx, viewportHeight);
    topPx = Math.max(VIEWPORT_PADDING_PX, Math.min(topPx, maxTop));
    return {
      position: 'fixed',
      top: `${topPx}px`,
      bottom: 'auto',
      left: horizontal.left,
      right: horizontal.right,
      zIndex: '1200',
    };
  }

  let bottomPx = viewportHeight - fieldRect.top + PANEL_GAP_PX;
  const maxBottom =
    viewportHeight - VIEWPORT_PADDING_PX - Math.min(panelHeightPx, viewportHeight);
  bottomPx = Math.max(VIEWPORT_PADDING_PX, Math.min(bottomPx, maxBottom));
  return {
    position: 'fixed',
    top: 'auto',
    bottom: `${bottomPx}px`,
    left: horizontal.left,
    right: horizontal.right,
    zIndex: '1200',
  };
}

function resolveHorizontalAnchors(
  fieldRect: DOMRect,
  align: 'start' | 'end',
  viewportWidth: number,
  panelWidthPx?: number,
): { left: string; right: string } {
  if (align === 'end') {
    // Flush panel right edge to field right edge.
    let rightPx = viewportWidth - fieldRect.right;
    rightPx = Math.max(VIEWPORT_PADDING_PX, rightPx);

    if (panelWidthPx != null && panelWidthPx > 0) {
      const naturalLeft = fieldRect.right - panelWidthPx;
      if (naturalLeft < VIEWPORT_PADDING_PX) {
        // Prefer staying on-screen when the panel is wider than space to the left.
        return {
          left: `${VIEWPORT_PADDING_PX}px`,
          right: `${rightPx}px`,
        };
      }
    }

    return {
      left: 'auto',
      right: `${rightPx}px`,
    };
  }

  // Start-align: flush panel left edge to field left edge.
  let leftPx = fieldRect.left;
  leftPx = Math.max(VIEWPORT_PADDING_PX, leftPx);

  if (panelWidthPx != null && panelWidthPx > 0) {
    const naturalRight = leftPx + panelWidthPx;
    if (naturalRight > viewportWidth - VIEWPORT_PADDING_PX) {
      return {
        left: `${leftPx}px`,
        right: `${VIEWPORT_PADDING_PX}px`,
      };
    }
  }

  return {
    left: `${leftPx}px`,
    right: 'auto',
  };
}
